/* anti-inspect.js v2.3 (Shopify hardened, no-blur)
   Goals:
   - Make casual inspection annoying and slow
   - Keep site fully visible/usable for real shoppers
   - Bypass for Theme Editor; staff kill switches
   Features:
   - Blocks: context menu (except inputs), dragstart on images, user-select on body (re-enabled for inputs)
   - Blocks hotkeys: F12, Ctrl+Shift+I/J/C, Ctrl/Cmd+U (view source), Ctrl/Cmd+S (save), Ctrl/Cmd+P (print)
   - DevTools detection: window dock delta, rAF stall, setInterval stall, console getter trip
   - While DevTools detected: force-disable copy/cut/selection/drag; show small toast (optional)
   - Staff kill switches: ?antiinspect=off  |  localStorage.antiinspect="off"  |  cookie antiinspect=off
   Notes:
   - Nothing here is bulletproof; this just raises the effort a lot.
*/

(function(){
  'use strict';
  const DOC = document, WIN = window, ROOT = DOC.documentElement;

  // ---------- Config ----------
  const CFG = {
    // Deterrents
    blockContextMenu: true,
    blockShortcuts: true,
    blockCopyCut: true,
    blockSelect: true,
    blockDragOnImages: true,

    // DevTools heuristics
    dockDeltaThreshold: 160,      // outer vs inner size difference
    stallThresholdMs: 900,        // interval/rAF stall threshold
    tickMs: 220,                  // base polling
    jitterMs: 180,                // random jitter
    toastMs: 2200,                // toast visible time

    // Staff bypass
    allowUrlDisable: true,
    urlKey: 'antiinspect',
    urlValueOff: 'off',

    // Toast text
    toastMsg: 'Developer tools detected. Copy/drag disabled while active.'
  };

  // ---------- Bypass (Theme Editor or staff kill switch) ----------
  function bypassActive(){
    try {
      if (WIN.Shopify && Shopify.designMode) return true; // Theme Editor
      const p = new URLSearchParams(location.search);
      if (CFG.allowUrlDisable && p.get(CFG.urlKey) === CFG.urlValueOff) return true;
      if (localStorage.getItem('antiinspect') === 'off') return true;
      if (DOC.cookie.split('; ').some(v => v.trim() === 'antiinspect=off')) return true;
    } catch(e){}
    return false;
  }
  if (bypassActive()) return;

  // ---------- Helpers ----------
  function once(el, ev, cb, opts){ const f = (e)=>{ el.removeEventListener(ev,f,opts); cb(e); }; el.addEventListener(ev,f,opts); }
  function addToast(){
    if (DOC.getElementById('ai-toast')) return;
    const el = DOC.createElement('div');
    el.id = 'ai-toast';
    el.textContent = CFG.toastMsg;
    (DOC.body || ROOT).appendChild(el);
    // Minimal inline style fallback if no CSS asset
    el.style.position ||= 'fixed';
    el.style.bottom ||= '16px';
    el.style.left ||= '50%';
    el.style.transform ||= 'translateX(-50%)';
    el.style.maxWidth ||= '90vw';
    el.style.background ||= 'rgba(20,20,20,.92)';
    el.style.color ||= '#fff';
    el.style.padding ||= '10px 14px';
    el.style.font ||= '600 13px/1.3 system-ui,-apple-system,Segoe UI,Roboto,Arial';
    el.style.borderRadius ||= '10px';
    el.style.zIndex ||= '2147483647';
    el.style.display ||= 'none';
  }
  function showToast(){
    const t = DOC.getElementById('ai-toast'); if (!t) return;
    t.classList.add('show'); t.style.display = 'block';
    clearTimeout(showToast._t); showToast._t = setTimeout(()=>{ t.classList.remove('show'); t.style.display = 'none'; }, CFG.toastMs);
  }

  // ---------- Input deterrents ----------
  function allowInputTag(tag){ return tag === 'input' || tag === 'textarea'; }

  function installContextMenuBlock(){
    if (!CFG.blockContextMenu) return;
    DOC.addEventListener('contextmenu', (e)=>{
      const tag = (e.target && e.target.tagName || '').toLowerCase();
      if (allowInputTag(tag)) return; // keep input menus
      e.preventDefault();
    }, {capture:true});
  }

  function installSelectCopyBlocks(){
    if (CFG.blockSelect) {
      DOC.addEventListener('selectstart', (e)=>{
        const tag = (e.target && e.target.tagName || '').toLowerCase();
        if (allowInputTag(tag)) return;
        e.preventDefault();
      }, {capture:true});
    }
    if (CFG.blockCopyCut) {
      DOC.addEventListener('copy', (e)=>{ e.preventDefault(); }, {capture:true});
      DOC.addEventListener('cut',  (e)=>{ e.preventDefault(); }, {capture:true});
    }
  }

  function installDragBlock(){
    if (!CFG.blockDragOnImages) return;
    DOC.addEventListener('dragstart', (e)=>{
      const t = e.target;
      if (t && (t.tagName||'').toLowerCase() === 'img') e.preventDefault();
    }, {capture:true});
  }

  function installShortcutBlock(){
    if (!CFG.blockShortcuts) return;
    DOC.addEventListener('keydown', (e)=>{
      const k = (e.key||'').toLowerCase();
      const code = e.keyCode || e.which;
      const ctrl = !!e.ctrlKey, shift = !!e.shiftKey, meta = !!e.metaKey, alt = !!e.altKey;

      // DevTools
      if (code === 123) { e.preventDefault(); e.stopPropagation(); return; }            // F12
      if (ctrl && shift && (k === 'i' || k === 'j' || k === 'c')) {                    // Ctrl+Shift+I/J/C
        e.preventDefault(); e.stopPropagation(); return;
      }
      if ((meta && alt && (k === 'i' || k === 'c')) || (meta && alt && shift && k === 'i')) { // mac variants
        e.preventDefault(); e.stopPropagation(); return;
      }

      // View Source / Save / Print
      if ((ctrl || meta) && (k === 'u' || k === 's' || k === 'p')) {
        e.preventDefault(); e.stopPropagation(); return;
      }
    }, {capture:true});
  }

  // ---------- DevTools detection (multi-heuristic) ----------
  const State = { open:false, lastTick: performance.now(), lastRAF: performance.now() };

  // A: window dock delta
  function dockLikely(){
    const dW = Math.abs(WIN.outerWidth - WIN.innerWidth);
    const dH = Math.abs(WIN.outerHeight - WIN.innerHeight);
    return dW > CFG.dockDeltaThreshold || dH > CFG.dockDeltaThreshold;
    // Note: undocked DevTools won’t trigger this; other heuristics cover that.
  }

  // B: timer stall (debugger pause / breakpoint)
  function timerStalled(){
    const now = performance.now();
    const stalled = (now - State.lastTick) > CFG.stallThresholdMs;
    State.lastTick = now;
    return stalled;
  }

  // C: rAF stall (heavy devtools or throttling)
  let rafStall = false;
  function rafLoop(){
    const now = performance.now();
    if (now - State.lastRAF > CFG.stallThresholdMs) rafStall = true;
    State.lastRAF = now;
    WIN.requestAnimationFrame(rafLoop);
  }
  WIN.requestAnimationFrame(rafLoop);

  // D: console getter trip
  function consoleProbe(){
    let tripped = false;
    try {
      const bait = new Image();
      Object.defineProperty(bait, 'id', { get(){ tripped = true; return 'x'; }});
      // Writing a styled object can also hint in some consoles
      console.log('%c', bait);
    } catch(e){}
    return tripped;
  }

  function devtoolsDetected(){
    return dockLikely() || timerStalled() || rafStall || consoleProbe();
  }

  // ---------- Reaction while DevTools are open ----------
  function setDevtoolsMode(on){
    if (on === State.open) return;
    State.open = on;
    if (on) {
      // Harden: disable selection / copy / drag quickly
      ROOT.setAttribute('data-ai-devtools', '1');
      showToast();
    } else {
      ROOT.removeAttribute('data-ai-devtools');
    }
  }

  function tick(){
    try { setDevtoolsMode(devtoolsDetected()); } catch(e){}
    const delay = CFG.tickMs + Math.random()*CFG.jitterMs;
    setTimeout(tick, delay);
  }

  // ---------- Init ----------
  function init(){
    addToast();
    installContextMenuBlock();
    installSelectCopyBlocks();
    installDragBlock();
    installShortcutBlock();
    tick();
  }

  if (DOC.readyState === 'loading') {
    once(DOC, 'DOMContentLoaded', init, {once:true});
  } else {
    init();
  }

  // Expose tiny toggle for yourself in console (if you get in): window._aiOff()
  WIN._aiOff = function(){
    try { localStorage.setItem('antiinspect','off'); DOC.cookie = 'antiinspect=off;path=/;max-age=31536000'; } catch(e){}
    location.reload();
  };
})();
